import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const BudgetIncreaseEfficiencyChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, isLoading } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const budgetIncreases = events.filter((e: any) => {
            if (!(e.type || e.eventType)?.includes('budget')) return false;
            const prev = e.previousState?.find?.((s: any) => s.type === 'budget');
            const next = e.nextState?.find?.((s: any) => s.type === 'budget');
            return prev && next && Number(next.value) > Number(prev.value);
        });

        if (budgetIncreases.length === 0) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const results: { name: string; roas: number }[] = [];

        budgetIncreases.forEach((event: any) => {
            const eventDate = event.date?.split('T')[0];
            if (!eventDate) return;
            const dateIdx = sortedDates.indexOf(eventDate);
            if (dateIdx < 0) return;

            const beforeDays = sortedDates.slice(Math.max(0, dateIdx - 7), dateIdx);
            const afterDays = sortedDates.slice(dateIdx, Math.min(sortedDates.length, dateIdx + 7));

            const revBefore = beforeDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.revenue || 0), 0);
            const revAfter = afterDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.revenue || 0), 0);
            const spendBefore = beforeDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.adSpend || 0), 0);
            const spendAfter = afterDays.reduce((s, d) => s + (finance.dailyBreakdown![d]?.metrics.adSpend || 0), 0);

            const incrementalSpend = spendAfter - spendBefore;
            const incrementalRevenue = revAfter - revBefore;
            const incrROAS = incrementalSpend > 0 ? incrementalRevenue / incrementalSpend : 0;

            const label = event.description || format(parseISO(eventDate), 'MMM dd');
            results.push({ name: label.length > 18 ? label.substring(0, 18) + '...' : label, roas: Number(incrROAS.toFixed(2)) });
        });

        return results.slice(0, 10);
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ ...CHART_STYLES.margin.withAxis, left: 80 }}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} horizontal={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis type="number" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} tickFormatter={(v) => `${v}x`} />
                        <YAxis dataKey="name" type="category" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={80} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={(v: number) => [`${v}x`, t('charts.incrementalROAS', 'Incremental ROAS')]} />
                        <ReferenceLine x={1} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                        <Bar dataKey="roas" radius={[0, 3, 3, 0]} maxBarSize={24}>
                            {chartData.map((entry, i) => (
                                <Cell key={i} fill={entry.roas >= 1 ? CHART_COLORS.revenue : CHART_COLORS.danger} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
